import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useLocation, useParams } from 'react-router-dom';
import CreateOptionModal from '@/components/modal/modal-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import createCompanyOptionApi from '@/api/post/company/company-option/create-company-option';
import Alert from '@/components/alert/alert-component';
import processOptionData, { processSubmitData } from './options-utils';
import OptionsForm from './options-form';

const Main = styled.div`
  background-color: ${({ theme }) => theme.COLOR_BG_DARK};
  min-height: 100%;
  justify-content: center;
  width: 100%;
`;

/**
 * @param {object} root0 - The props object
 * @param {object} root0.modalAction - Modal action configuration
 * @param {Function} root0.closeModal - Function to close the modal
 * @param {boolean} root0.loading - Loading state
 * @param {Function} root0.onCreate - Callback function called when option is created
 * @returns {JSX.Element}
 */
const OptionModal = ({
  modalAction,
  closeModal,
  loading = false,
  onCreate = null,
}) => {
  const { id } = useParams();
  const { state: locationState } = useLocation();
  const [createCompanyOption, { loading: createCompanyOptionLoading }] =
    useMutation(createCompanyOptionApi);

  /**
   * Handles the company option creation process
   *
   * @param {object} submitValues - The processed form data ready for API submission
   * @param {string} submitValues.name - Option name
   * @param {string} submitValues.description - Option description
   * @param {Array} submitValues.variants - Option variants/choices
   * @param {object} submitValues.pricing - Pricing configuration
   */
  const handleCreateCompanyOption = (submitValues) => {
    createCompanyOption(submitValues, id)
      .then((response) => {
        if (onCreate) {
          // Modal mode - call the callback with the created option
          onCreate({
            optionId: response.data.id,
            optionPriceLinkId: response.data.option_price_link_id,
          });
        }

        Alert({
          icon: 'success',
          text: 'Option created successfully',
        });

        closeModal();
      })
      .catch(() => {
        Alert({
          icon: 'error',
          text: 'Failed to create option',
        });
      });
  };

  const submitForm = async (values) => {
    const submitValues = await processSubmitData(values, 'add');

    handleCreateCompanyOption(submitValues);
  };

  const isLoading = createCompanyOptionLoading || loading;

  return (
    <CreateOptionModal
      open={!!modalAction.label}
      close={closeModal}
      width="95%"
      heigh="95%"
      alignItems="flex-start"
      title={`${modalAction.label} Option`}
      titleFontSize="28px"
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        padding: 0,
      }}
      isLoading={isLoading}
      minHeight="400px"
    >
      <Main>
        <FormWrapper
          defaultValues={async () =>
            // Process initial form data, handling duplication if needed
            processOptionData(id, locationState?.isOptionDuplicate, {
              isEdit: false,
              isDuplicate: !!locationState?.isOptionDuplicate,
            })
          }
          onSubmit={submitForm}
        >
          <OptionsForm
            type="create"
            modal
            submitForm={submitForm}
            isLoading={isLoading}
            closeModal={closeModal}
          />
        </FormWrapper>
      </Main>
    </CreateOptionModal>
  );
};

export default OptionModal;

OptionModal.propTypes = {
  modalAction: PropTypes.object.isRequired,
  closeModal: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onCreate: PropTypes.func,
};

OptionModal.defaultProps = {
  loading: false,
  onCreate: null,
};
